import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16 }}>{String(this.state.error)}</div>
          <button className="btn btn-primary" onClick={() => this.setState({ error: null })}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import Positions from './components/Positions/Positions';
import OrderLogs from './components/OrderLogs/OrderLogs';
import StrategyControl from './components/Strategies/StrategyControl';
import RiskPanel from './components/RiskPanel/RiskPanel';
import PriceAlerts from './components/Alerts/PriceAlerts';
import TradeJournal from './components/Journal/TradeJournal';
import NewsStocks from './components/News/NewsStocks';
import AppLogs from './components/AppLogs/AppLogs';
import SwingTrader from './components/SwingTrader/SwingTrader';
import AITrading from './components/AITrading/AITrading';
import Header from './components/Header/Header';
import { APP_VERSION, BUILD_DATE } from './version';
import './App.css';

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('npz_token');
  return token ? children : <Navigate to="/login" replace />;
}

function Sidebar({ isOpen, onClose }) {
  const links = [
    { to: '/',           label: '📊 Dashboard'  },
    { to: '/positions',  label: '💼 Positions'   },
    { to: '/orders',     label: '📋 Orders'      },
    { to: '/strategies', label: '⚙️ Strategies'  },
    { to: '/risk',       label: '🛡️ Risk'        },
    { to: '/alerts',     label: '🔔 Alerts'      },
    { to: '/swing',      label: '📈 Swing Trader' },
    { to: '/ai',         label: '🤖 AI Trading'   },
    // { to: '/journal', label: '📓 Journal'     },   // hidden — re-enable when needed
    { to: '/news',       label: '📰 News'        },
    { to: '/logs',       label: '🪵 Logs'        },
  ];
  return (
    <nav className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>
      <div className="sidebar-logo">
        <img src="/logo.svg" alt="NPZ TradePro" style={{ width: 210, height: 'auto', display: 'block' }} />
      </div>
      {links.map(l => (
        <NavLink
          key={l.to} to={l.to} end
          className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
          onClick={onClose}
        >
          {l.label}
        </NavLink>
      ))}
      <button
        className="logout-btn"
        onClick={() => { localStorage.removeItem('npz_token'); window.location.href = '/login'; }}
      >
        🚪 Logout
      </button>
      {/* Version badge at bottom of sidebar */}
      <div style={{
        marginTop: 'auto', padding: '12px 16px',
        borderTop: '1px solid var(--border)',
        fontSize: 11, color: 'var(--muted)', lineHeight: 1.6,
      }}>
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 99,
          background: 'rgba(99,102,241,0.15)', color: 'var(--accent)',
          border: '1px solid rgba(99,102,241,0.3)', fontWeight: 700, fontSize: 11,
          marginBottom: 4,
        }}>
          v{APP_VERSION}
        </span>
        <div style={{ color: 'var(--muted)', fontSize: 10 }}>Built {BUILD_DATE}</div>
      </div>
    </nav>
  );
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on route change (mobile nav)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  return (
    <div className="app-layout">
      {/* Mobile overlay — tap to close sidebar */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Header onMenuClick={() => setSidebarOpen(o => !o)} />
      <main className="main-content" style={{ paddingTop: 76 }}>
        {children}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary>
                  <Routes>
                    <Route path="/"           element={<Dashboard />}       />
                    <Route path="/positions"  element={<Positions />}       />
                    <Route path="/orders"     element={<OrderLogs />}       />
                    <Route path="/strategies" element={<StrategyControl />} />
                    <Route path="/risk"       element={<RiskPanel />}       />
                    <Route path="/alerts"     element={<PriceAlerts />}     />
                    <Route path="/swing"      element={<SwingTrader />}     />
                    <Route path="/ai"         element={<AITrading />}       />
                    <Route path="/journal"    element={<TradeJournal />}    />
                    <Route path="/news"       element={<NewsStocks />}      />
                    <Route path="/logs"       element={<AppLogs />}         />
                  </Routes>
                </ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
